/**
 * Report list/card blurb — one-line summary for Reports tab rows and
 * Overview cards. Falls through several report fields so feasibility
 * reports with empty `summary` but populated `executiveSummary` still
 * show useful text.
 */
import type { SiteRagReport } from '../types/siteRag';
import { getBtmDecisionSummary } from './btmDecision';
import { detectInstructionShape } from './instructionShapeGuard';
import { coerceNarrativeText, isRenderableNarrative } from './safeNarrativeProse';
import { readReportFindings } from './reportFindings';
import { splitSentences } from './citationDensity';
import { resolveEffectiveConclusion } from './resolveEffectiveConclusion';

/**
 * FIX 4 — truncate a card blurb to its first `maxSentences` sentences so the
 * Reports-tab cards show a tight 2-sentence teaser instead of the full report
 * abstract (a wall of text). Sentence-boundary aware via `splitSentences`
 * (keeps the terminal `.`/`!`/`?`). When the source has fewer sentences than
 * the cap it is returned verbatim; an unpunctuated blob (one "sentence") is
 * passed through unchanged and the caller's CSS `line-clamp` backstops it.
 *
 * Returns null for empty input so callers can skip rendering entirely.
 */
export function firstSentences(
  text: string | null | undefined,
  maxSentences = 2,
): string | null {
  if (typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed) return null;
  const sentences = splitSentences(trimmed);
  if (sentences.length <= maxSentences) return trimmed;
  return sentences.slice(0, maxSentences).join(' ');
}

export function getReportCardSummary(report: SiteRagReport | undefined): string | null {
  if (!report) return null;

  const summary = coerceNarrativeText(report.summary).trim();
  if (summary) return summary;

  const intentOverview = coerceNarrativeText(report.projectIntentOverview).trim();
  if (intentOverview && isRenderableNarrative(intentOverview)) {
    return intentOverview.split(/\n\n+/)[0]?.trim() ?? intentOverview;
  }

  const executive = coerceNarrativeText(report.executiveSummary).trim();
  if (executive && !detectInstructionShape(executive).flagged) return executive;

  const recommendation = report.conclusion?.recommendation?.trim();
  if (recommendation && recommendation !== '—') return recommendation;

  const { items } = readReportFindings(report, 1, { truncateAt: 'none' });
  if (items.length > 0 && items[0]?.trim()) return items[0].trim();

  const decision = getBtmDecisionSummary(report).trim();
  if (decision && decision !== 'Decision summary pending' && decision !== 'Decision pending') return decision;

  return null;
}

/** A decision-summary string we treat as "no real content" (the pending /
 *  placeholder states from getBtmDecisionSummary + conclusion fallbacks). */
function isPlaceholderDecision(s: string): boolean {
  const t = s.trim().toLowerCase();
  return (
    t === '' ||
    t === 'decision pending' ||
    t === 'decision summary pending' ||
    t === '—' ||
    t === 'pending'
  );
}

/**
 * aurora-qa-3 (⑥) / #56 — a MEANINGFUL report-card blurb: a complete 1–3
 * sentence summary that reflects the report's ACTUAL conclusion, routed
 * through the SAME SSOT the report view's `ConclusionCallout` uses
 * (`resolveEffectiveConclusion`). Order of preference:
 *
 *   1. The BTM decision summary (`btmDecisionSummary` /
 *      `conclusion.btmDecisionSummary` / viability / grid-vs-btm advantage) —
 *      this is the report's own verdict rationale and the most decision-useful
 *      line for a card.
 *   2. The resolved conclusion's PROSE — `btmDecisionSummary` /
 *      `environmentalVerdict` from `resolveEffectiveConclusion(report)`. This
 *      keeps the card in lock-step with the report view rather than reading the
 *      raw stored `report.conclusion`, which can diverge.
 *   3. The general prose summary (abstract / exec summary / intent overview /
 *      recommendation / findings) via `getReportCardSummary`.
 *   4. LAST RESORT — the recommendation verdict alone, optionally paired with a
 *      CALM strength / opportunity. We deliberately do NOT pull a raw
 *      `topRisks` / `keyConditions` bullet as the headline driver: composing
 *      e.g. "Do Not Proceed. NRC Reactor Proximity: Nearest reactor: Grand Gulf
 *      (75.6km)…" pairs the verdict with an out-of-context raw metric and reads
 *      as a scary, non-narrative line that diverges from the report's actual
 *      conclusion (#56 root cause).
 *
 * Whatever source wins is reduced to its first `maxSentences` COMPLETE
 * sentences (sentence-boundary, never mid-word) so the CSS `line-clamp-3`
 * backstop on the card only ever truncates whole lines of a clean sentence
 * boundary — not a word fragment. Returns null when nothing meaningful exists.
 */
export function getReportCardDecisionSummary(
  report: SiteRagReport | undefined,
  maxSentences = 3,
): string | null {
  if (!report) return null;

  // 1 — decision summary / verdict rationale (the most decision-useful line).
  const decision = getBtmDecisionSummary(report).trim();
  if (!isPlaceholderDecision(decision)) {
    return firstSentences(decision, maxSentences);
  }

  // 2 — the resolved conclusion's PROSE (same SSOT as ConclusionCallout). Use
  //     btmDecisionSummary / environmentalVerdict prose so the card states the
  //     report's actual conclusion, not a stale/raw composition.
  const effective = resolveEffectiveConclusion(report);
  const resolvedProse =
    effective?.btmDecisionSummary?.trim() ||
    effective?.environmentalVerdict?.trim() ||
    '';
  if (resolvedProse) {
    return firstSentences(resolvedProse, maxSentences);
  }

  // 3 — fall back to the abstract's opening COMPLETE sentences.
  const abstract = getReportCardSummary(report);
  if (abstract && abstract.trim()) {
    return firstSentences(abstract, maxSentences);
  }

  // 4 — LAST RESORT: the recommendation verdict, optionally paired with a CALM
  //     strength / opportunity. Never a raw risk / condition bullet (#56).
  const verdict = effective?.recommendation?.trim();
  if (verdict && verdict !== '—') {
    const calmDriver =
      effective?.topStrengths?.find((s) => s?.trim())?.trim() ??
      effective?.keyOpportunities?.find((s) => s?.trim())?.trim() ??
      null;
    const composed = calmDriver ? `${verdict}. ${calmDriver}` : verdict;
    return firstSentences(composed, maxSentences);
  }

  return null;
}
